"use client";

import { useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stat = {
  prefix: string;
  value: number;
  suffix: string;
  label: string;
  description: string;
};
const stats: Stat[] = [{
  prefix: "+",
  value: 35,
  suffix: "%",
  label: "REVENUE GROWTH",
  description: "Average increase in RevPAR achieved through our data-driven OTA optimization strategies."
}, {
  prefix: "",
  value: 250,
  suffix: "+",
  label: "HOTELS OPTIMIZED",
  description: "Properties across India and beyond that have successfully scaled their digital presence."
}, {
  prefix: "",
  value: 15,
  suffix: " YRS",
  label: "INDUSTRY EXPERTISE",
  description: "Over a decade of hands-on experience in hotel revenue management and hospitality tech."
}, {
  prefix: "",
  value: 95,
  suffix: "%",
  label: "CLIENT RETENTION",
  description: "Long-term partnerships built on consistent performance and transparent revenue reporting."
}];
function CountUp({
  target,
  duration = 1200,
  start,
  prefix,
  suffix
}: {
  target: number;
  duration?: number;
  start: boolean;
  prefix: string;
  suffix: string;
}) {
  const [count, setCount] = React.useState(0);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (!start) return;
    const startTime = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(target * eased));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [start, target, duration]);
  return <span>
      {prefix}
      {count}
      {suffix}
    </span>;
}
import React from "react";
export default function MarketChallengesStats() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2,
    once: true
  });
  return <section data-section-id="667" id="marketchallengesstats" ref={sectionRef} className="relative overflow-hidden py-24 md:py-32 lg:py-44 px-6 md:px-10 lg:px-16 bg-slate-900 text-white">
      <svg aria-hidden="true" className="pointer-events-none absolute -bottom-10 -right-10 hidden md:block opacity-15" width="420" height="420" viewBox="0 0 420 420" fill="none" stroke="currentColor" strokeWidth="1">
        <rect x="40" y="40" width="340" height="340" />
        <rect x="80" y="80" width="260" height="260" />
        <line x1="40" y1="210" x2="380" y2="210" />
        <line x1="210" y1="40" x2="210" y2="380" />
        <circle cx="210" cy="210" r="110" />
        <line x1="80" y1="80" x2="340" y2="340" />
        <line x1="340" y1="80" x2="80" y2="340" />
      </svg>

      <div className="relative mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 xl:gap-20">
          <div className={`lg:col-span-5 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="text-xs font-default uppercase tracking-[0.2em] mb-8 text-amber-500">
              02 — Impact
            </div>

            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-8 text-white">
              Data-driven revenue,
              <br />
              measured in
              <br />
              <em className="italic font-light">measurable growth.</em>
            </h2>

            <p className="text-base md:text-lg leading-relaxed mb-10 max-w-md font-default text-slate-300">
              We transform hotel operations through precision revenue management. Every strategy at HotelRevBaBa is a calculated move to maximize your OTA performance and direct booking potential.
            </p>

            <Link to="#" className="group inline-flex items-center gap-3 text-sm font-medium tracking-wide pb-1 border-b transition-colors duration-300 text-white border-white/40">
              <span>Explore our services</span>
              <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 md:grid-cols-2">
              {stats.map((stat, i) => {
              const isTop = i < 2;
              const isLeft = i % 2 === 0;
              return <div key={i} data-index={i} className={`relative p-8 md:p-10 lg:p-12 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
                transitionDelay: `${200 + i * 150}ms`
              }}>
                    {!isTop && <div className="absolute top-0 left-0 right-0 h-px bg-slate-700" />}
                    {i > 0 && <div className="absolute top-0 left-0 right-0 h-px md:hidden bg-slate-700" />}
                    {isLeft && <div className="absolute top-0 bottom-0 right-0 w-px hidden md:block bg-slate-700" />}

                    <div className="font-default text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-none mb-5 tracking-tight text-amber-500">
                      <CountUp target={stat.value} start={isVisible} prefix={stat.prefix} suffix={stat.suffix} />
                    </div>
                    <div className="text-[11px] font-default uppercase tracking-[0.18em] mb-4 text-slate-400">
                      {stat.label}
                    </div>
                    <p className="text-sm leading-relaxed max-w-xs font-default text-slate-400">
                      {stat.description}
                    </p>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}